import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from '../../api'
import { StatusCodes } from '../../http-status-codes'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ScanStatusDto, FoundDevicesDto } from './dto/device-detector.dto'
import { ScanStatusModel, FoundDevicesModel } from '../models/device-detector.model'
import { scanStatusDtoToModelTransformer, foundDevicesDtoToModelTransformer } from './transformer/device-detector-to-model.transformer'

/**
 * Initiates a network scan for connected qntm storage units.
 * POST /storages/$SN/hardware/device-detector/scan
 */
export const initiateDeviceDetectorScan = async (
  authFetch: AuthFetchWithResponse,
  emcSerialnumber: string | undefined,
  abortSignal?: AbortSignal
): Promise<ApiResult<void, AuthFetchError | Error>> => {
  try {
    if (!emcSerialnumber) {
      return handleFetchError(new Error('emcSerialnumber missing'))
    }

    const response = await authFetch<void>({
      path: `storages/${emcSerialnumber}/hardware/device-detector/scan`,
      method: 'POST',
      abortSignal,
    })

    // Scan status polling must start only after backend acknowledged the trigger with 204.
    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void, AuthFetchError | Error>(undefined)
    }

    return new ApiResult<void, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

/**
 * Gets the current device detector scan status and discovered devices.
 * GET /storages/$SN/hardware/device-detector/scan
 */
export const fetchDeviceDetectorScanStatus = async (
  authFetch: AuthFetchWithResponse,
  emcSerialnumber: string | undefined
): Promise<ApiResult<ScanStatusModel, AuthFetchError | Error>> => {
  try {
    if (!emcSerialnumber) {
      return handleFetchError(new Error('emcSerialnumber missing'))
    }

    const response = await authFetch<ScanStatusDto>({
      path: `storages/${emcSerialnumber}/hardware/device-detector/scan`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ScanStatusModel, AuthFetchError | Error>(scanStatusDtoToModelTransformer(response.data))
    }

    return new ApiResult<ScanStatusModel, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ScanStatusModel>(error)
  }
}

/**
 * Fetches the previously saved device detector scan results.
 * GET /storages/$SN/hardware/device-detector/found-devices
 */
export const fetchFoundDevices = async (
  authFetch: AuthFetchWithResponse,
  emcSerialnumber: string | undefined
): Promise<ApiResult<FoundDevicesModel, AuthFetchError | Error>> => {
  try {
    if (!emcSerialnumber) {
      return handleFetchError(new Error('emcSerialnumber missing'))
    }

    const response = await authFetch<FoundDevicesDto>({
      path: `storages/${emcSerialnumber}/hardware/device-detector/found-devices`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<FoundDevicesModel, AuthFetchError | Error>(foundDevicesDtoToModelTransformer(response.data))
    }

    return new ApiResult<FoundDevicesModel, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<FoundDevicesModel>(error)
  }
}

/**
 * Saves the current scan results to the database.
 * POST /storages/$SN/hardware/device-detector/found-devices
 */
export const saveFoundDevices = async (
  authFetch: AuthFetchWithResponse,
  emcSerialnumber: string | undefined,
  serialNumbers: string[]
): Promise<ApiResult<void, AuthFetchError | Error>> => {
  try {
    if (!emcSerialnumber) {
      return handleFetchError(new Error('emcSerialnumber missing'))
    }

    const response = await authFetch<void>({
      path: `storages/${emcSerialnumber}/hardware/device-detector/found-devices`,
      method: 'POST',
      payload: JSON.stringify({ devices: serialNumbers.map(serialNumber => ({ serialNumber })) }),
    })

    if (response.status === StatusCodes.OK || response.status === StatusCodes.CREATED || response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void, AuthFetchError | Error>(undefined)
    }

    return new ApiResult<void, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}
